import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import classNames from 'classnames';
import _ from 'lodash';
import {Company, ILegalEntity} from '../../interface';
import InitTable, {Query} from '../Table/Table';
import TableMenu, {MenuItem} from '../TableMenu/TableMenu';
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper';
import {TOOLTIP_SECTIONS} from '../../enums';
import style from './LegalEntityTable.module.scss';

interface LegalEntity extends ILegalEntity {
  company: Company;
}

interface Props {
  data: LegalEntity[];
  loading: boolean;
}

const {Table, Column, TopBar} = InitTable<LegalEntity>();

function LegalEntityTable(props: Props) {
  const {data, loading} = props;
  const navigate = useNavigate();
  const [query, setQuery] = useState<Query>();
  function formatNameAndNumber(name?: string, number?: string) {
    return _.compact([name, number && `${number}`]).join(' | ');
  }

  function getMenuItems(property: LegalEntity) {
    const editCompany = property.companyId && {
      'Edit Company': () => {
        navigate(`/properties/editCompany/${property.companyId}`);
      },
    };
    const editLegalEntity = property.id && {
      'Edit Legal Entity': () => {
        navigate(`/properties/editLegalEntity/${property.id}`, {
          state: location.pathname + '?tab=Legal+Entities',
        });
      },
    };
    const actions = [_.assign({}, editCompany, editLegalEntity)];
    return [
      _.map(
        _.assign({}, ..._.compact(actions)),
        (onClick, label) =>
          ({
            variant: 'default',
            tooltip: {
              section: TOOLTIP_SECTIONS.TableRowAction,
              keyword: label,
            },
            label,
            onClick,
          } as MenuItem),
      ),
    ];
  }

  function fetchData(query?: Query) {
    setQuery(query);
  }

  function searchData(data: LegalEntity[], value: string | undefined) {
    if (value) {
      const escaped = _.escapeRegExp(value);
      return _.filter(
        data,
        (item) =>
          new RegExp(escaped, 'i').test(item.name) ||
          new RegExp(escaped, 'i').test(item.number) ||
          new RegExp(escaped, 'i').test(item.company?.name as string) ||
          new RegExp(escaped, 'i').test(item.company?.number as string)
      );
    } else {
      return data;
    }
  }
  return (
    <div className={style['table-layout']}>
      <Table
        paginate
        id='manageProperties'
        rows={
          _.nth(
            _.chunk(
              searchData(
                _.orderBy(
                  data,
                  _.last(_.keys(query?.order)),
                  _.last(_.values(query?.order)),
                ),
                query?.search,
              ),
              query?.pagination?.pageSize,
            ),
            (query?.pagination?.page as number) - 1,
          ) as LegalEntity[]
        }
        loading={loading}
        onQueryChanged={fetchData}
        sortable
        searchable='Search legal entities...'
        totalRows={searchData(data, query?.search).length}
      >
        <TopBar>
          <div className={style['operate']}>
            <TooltipWrapper
              tooltipSection={TOOLTIP_SECTIONS.TableAction}
              tooltipKey='Add Legal Entity'
            >
              <a
                className='button primary add-button'
                onClick={(e) => {
                  navigate('/properties/legalEntitySetup', {
                    state: location.pathname + '?tab=Legal+Entities',
                  });
                  e.stopPropagation();
                }}
              >
                <i></i>Add Legal Entity
              </a>
            </TooltipWrapper>
          </div>
        </TopBar>
        <Column id='number' label='Legal Entity Name & Number'>
          {(property: LegalEntity) =>
            formatNameAndNumber(property.name, property.number)
          }
        </Column>
        <Column id='company.number' label='Company Name & Number'>
          {(property: LegalEntity) =>
            formatNameAndNumber(
              property.company?.name,
              property.company?.number,
            )
          }
        </Column>
        <Column id='active' label='Status'>
          {(property: LegalEntity) => (
            <span
              className={classNames(
                'badge-status',
                _.kebabCase(property.active ? 'Active' : 'Inactive'),
              )}
            >
              {property.active ? 'Active' : 'Inactive'}
            </span>
          )}
        </Column>
        <Column id='actions' label='Actions' sortable={false}>
          {(property: LegalEntity) => (
            <div className={style['actions-column-cell']}>
              <TableMenu
                toggle={
                  <button type='button'>
                    <i className={style['icon-3-dots']} />
                  </button>
                }
                menuItems={getMenuItems(property)}
              />
            </div>
          )}
        </Column>
      </Table>
    </div>
  );
}

export default LegalEntityTable;
